import type {ReactNode} from 'react';
import {useCallback, useState} from 'react';
import {FaXmark} from 'react-icons/fa6';
import {Description, Dialog, DialogPanel, DialogTitle} from '@headlessui/react';
import clsx from 'clsx';
import {AnimatePresence, motion} from 'motion/react';

import {Button} from '@/modules/foundation/components/button';
import {InfoCircleIcon} from '@/modules/foundation/components/icons/info-circle-icon';
import {Heading} from '@/modules/foundation/components/typography/heading';

type Props = {
	children: ReactNode;
};

const MotionDialogPanel = motion.create(DialogPanel);

export const WhyDoWeAskDialog = ({children}: Props) => {
	const [isOpen, setIsOpen] = useState(false);

	const closeDialog = useCallback(() => {
		setIsOpen(false);
	}, []);

	const openDialog = useCallback(() => {
		setIsOpen(true);
	}, []);

	return (
		<>
			<button
				onClick={openDialog}
				className={clsx(
					'text-primary-15',
					'text-[13px]',
					'md:text-[14px]',
					'font-semibold',
					'flex',
					'items-center',
					'gap-2',
				)}
			>
				<InfoCircleIcon
					aria-hidden
					className={clsx('text-xl', 'md:text-2xl')}
				/>
				Hvorfor spør vi om dette?
			</button>
			<AnimatePresence>
				{isOpen && (
					<Dialog
						open={isOpen}
						onClose={closeDialog}
						className={clsx('relative', 'z-50')}
					>
						{/* The backdrop, rendered as a fixed sibling to the panel container */}
						<motion.div
							initial={{opacity: 0}}
							animate={{opacity: 1}}
							exit={{opacity: 0}}
							className="fixed inset-0 bg-primary-2/20"
							aria-hidden="true"
						/>

						{/* Full-screen container to center the panel */}
						<div
							className={clsx(
								'fixed',
								'inset-0',
								'flex',
								'w-screen',
								'pr-4',
							)}
						>
							<MotionDialogPanel
								initial={{left: '-100%'}}
								animate={{left: '0%'}}
								exit={{left: '-100%'}}
								transition={{
									type: 'spring',
									bounce: 0.25,
								}}
								className={clsx(
									'max-w-[870px]',
									'w-full',
									'bg-white',
									'relative',
								)}
							>
								<div
									className={clsx(
										'h-[62px]',
										'md:h-[82px]',
										'px-4',
										'md:px-8',
										'border-b',
										'border-grey-border',
										'flex',
										'items-center',
										'gap-4',
										'justify-between',
									)}
								>
									<Heading asChild size="md">
										<DialogTitle
											className={clsx('font-bold')}
										>
											Hvorfor spør vi om dette?
										</DialogTitle>
									</Heading>

									<Button
										variant="secondary"
										onClick={closeDialog}
										aria-label="lukk"
										className={clsx('p-2', 'md:p-2')}
									>
										<FaXmark aria-hidden />
									</Button>
								</div>

								<Description
									as="div"
									className={clsx(
										'overflow-y-auto',
										'py-6',
										'px-4',
										'md:px-14',
										'max-h-[calc(100dvh-62px)]',
										'md:max-h-[calc(100dvh-82px)]',
									)}
								>
									{children}
								</Description>
							</MotionDialogPanel>
						</div>
					</Dialog>
				)}
			</AnimatePresence>
		</>
	);
};
